import { useContext, useState, type JSX, type ReactNode } from 'react'

import {
  layOut,
  type BlockNode,
  type CellNode,
  type InlineNode
} from '../lib/layout.ts'
import { DocsNavigateContext } from './navigate.ts'

function CopyButton({
  text,
  testId
}: {
  text: string
  testId: string
}): JSX.Element {
  const [label, setLabel] = useState('Copy')
  return (
    <button
      type="button"
      className="docs-copy"
      data-testid={testId}
      onClick={() => {
        const clipboard = navigator.clipboard
        if (!clipboard) {
          setLabel('Copy failed')
          return
        }
        let pending: Promise<void>
        try {
          pending = clipboard.writeText(text)
        } catch {
          setLabel('Copy failed')
          return
        }
        void pending.then(
          () => setLabel('Copied'),
          () => setLabel('Copy failed')
        )
      }}
    >
      {label}
    </button>
  )
}

function CodeBlock({
  text,
  testId
}: {
  text: string
  testId: string
}): JSX.Element {
  return (
    <div className="docs-code-wrap">
      <CopyButton text={text} testId={testId} />
      <pre className="docs-code" data-testid={`${testId}-block`}>
        <code>{text}</code>
      </pre>
    </div>
  )
}

function InlineView({ nodes }: { nodes: readonly InlineNode[] }): ReactNode {
  return nodes.map(node => <InlineNodeView key={node.key} node={node} />)
}

function InlineNodeView({ node }: { node: InlineNode }): ReactNode {
  if (node.type === 'strong') {
    return (
      <strong>
        <InlineView nodes={node.children} />
      </strong>
    )
  }
  if (node.type === 'em') {
    return (
      <em>
        <InlineView nodes={node.children} />
      </em>
    )
  }
  if (node.type === 'code') {
    return <code className="docs-inline-code">{node.text}</code>
  }
  if (node.type === 'break') return <br />
  if (node.type === 'link') return <DocLink node={node} />
  return node.text
}

function DocLink({
  node
}: {
  node: Extract<InlineNode, { type: 'link' }>
}): JSX.Element {
  const navigate = useContext(DocsNavigateContext)
  if (!node.internal) {
    return (
      <a
        href={node.href}
        data-testid={node.testId}
        rel="noreferrer"
        target="_blank"
      >
        <InlineView nodes={node.children} />
      </a>
    )
  }
  return (
    <a
      href={node.href}
      data-testid={node.testId}
      onClick={event => navigate(event, node.href)}
    >
      <InlineView nodes={node.children} />
    </a>
  )
}

function Cells({
  cells,
  tag
}: {
  cells: readonly CellNode[]
  tag: 'td' | 'th'
}): JSX.Element {
  const Tag = tag
  return (
    <>
      {cells.map(cell => (
        <Tag key={cell.key}>
          <InlineView nodes={cell.children} />
        </Tag>
      ))}
    </>
  )
}

function Heading({
  level,
  children
}: {
  level: 2 | 3 | 4
  children: ReactNode
}): JSX.Element {
  if (level === 3) return <h3>{children}</h3>
  if (level === 4) return <h4>{children}</h4>
  return <h2>{children}</h2>
}

function BlockView({ node }: { node: BlockNode }): ReactNode {
  if (node.type === 'env') {
    return (
      <div className="docs-env-pair" data-testid={`env-pair-${node.pair}`}>
        {node.panels.map(panel => (
          <section
            key={panel.id}
            className={
              panel.id === 'sandbox'
                ? 'docs-env-sandbox'
                : 'docs-env-production'
            }
            data-testid={`env-${panel.id}-${node.pair}`}
          >
            <p
              className="docs-env-label"
              data-testid={`env-label-${panel.id}-${node.pair}`}
            >
              {panel.label}
            </p>
            <BlockView node={panel.node} />
          </section>
        ))}
      </div>
    )
  }
  if (node.type === 'heading') {
    return (
      <Heading level={node.level}>
        <InlineView nodes={node.children} />
      </Heading>
    )
  }
  if (node.type === 'paragraph') {
    return (
      <p>
        <InlineView nodes={node.children} />
      </p>
    )
  }
  if (node.type === 'code') {
    return <CodeBlock text={node.text} testId={node.testId} />
  }
  if (node.type === 'list') {
    const items = node.items.map(item => (
      <li key={item.key}>
        {item.children.map(child => (
          <BlockView key={child.key} node={child} />
        ))}
      </li>
    ))
    if (node.ordered) return <ol>{items}</ol>
    return <ul>{items}</ul>
  }
  if (node.type === 'table') {
    return (
      <div className="docs-table-wrap">
        <table>
          <thead>
            <tr>
              <Cells cells={node.header} tag="th" />
            </tr>
          </thead>
          <tbody>
            {node.rows.map(row => (
              <tr key={row.key}>
                <Cells cells={row.cells} tag="td" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }
  if (node.type === 'rule') return <hr />
  if (node.type === 'inline') return <InlineView nodes={node.children} />
  return node.text
}

export function MarkdownView({
  tokens
}: {
  tokens: Parameters<typeof layOut>[0]
}): JSX.Element {
  const blocks = layOut(tokens)
  return (
    <div data-testid="markdown-body">
      {blocks.map(node => (
        <BlockView key={node.key} node={node} />
      ))}
    </div>
  )
}
